import { Platform as FpB2bPlatform } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bMerchantService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-merchant-service'
import { type CountryCode, Platform, type ProjectCode } from '@constants'
import { useDebounceValue } from '@dni-hooks/useDebounceValue'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useControlled } from '@dni-react-hooks/use-controlled'
import { MuiAutocomplete, TextField, type TextFieldProps } from '@fortune-ui'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { useApiMerchantsSearch } from '@hooks-api/fortune/useApiMerchantsSearch'
import { useApiPartnerProfile } from '@hooks-api/fortune-fastpass/useApiPartnerProfile'
import ClearIcon from '@mui/icons-material/Clear'
import { type AutocompleteValue, IconButton, InputAdornment, type UseAutocompleteProps } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

interface Option {
  label: string
  value: number | null
  billingCountry: CountryCode | null
}

type AutocompleteProps = UseAutocompleteProps<Option, false, true, false>
type ValueProps = AutocompleteValue<Option, false, true, false>

export interface AutocompleteMerchantProps {
  projectCode: ProjectCode
  countryCode: CountryCode
  campaignId: number
  campaignCode?: string
  inputRef?: TextFieldProps['inputRef']
  error?: TextFieldProps['error']
  helperText?: TextFieldProps['helperText']
  onChange?: (value?: Option) => void
  defaultValue?: ValueProps
  value?: ValueProps
}

const useAutocompleteMerchantOptions = (args: {
  isShopManager: boolean
  merchantsSearchRequest: Parameters<typeof useApiMerchantsSearch>[0]
  partnerProfileRequest: Parameters<typeof useApiPartnerProfile>[0]
}): Option[] => {
  const { isShopManager, merchantsSearchRequest, partnerProfileRequest } = args
  const { destructuredData: merchants } = useApiMerchantsSearch(isShopManager ? merchantsSearchRequest : undefined)
  const { destructuredData: partnerProfile } = useApiPartnerProfile(!isShopManager ? partnerProfileRequest : undefined)

  return useMemo(() => {
    if (isShopManager) {
      return merchants?.map(merchant => ({
        label: merchant.billing_first_name,
        value: merchant.index,
        billingCountry: merchant.country_code,
      })) || []
    }
    return partnerProfile
      ? [{
        label: partnerProfile.billing_address_first_name,
        value: partnerProfile.id,
        billingCountry: partnerProfile.billing_country,
      }]
      : []
  }, [isShopManager, merchants, partnerProfile])
}

export const AutocompleteMerchant = (props: AutocompleteMerchantProps) => {
  const { projectCode, countryCode, campaignId, campaignCode, inputRef, error, helperText, onChange, defaultValue: defaultValueProp, value: valueProp } = props

  const { setMerchant, clearMerchant, merchant } = useFpB2bMerchantService(campaignId, FpB2bPlatform.STM)

  const defaultValue: ValueProps = defaultValueProp || {
    label: merchant.billingFirstName,
    value: merchant.index,
    billingCountry: merchant.billingCountry,
  }
  const [value, setValue] = useControlled<Option | undefined>({
    controlled: valueProp,
    default: defaultValue,
  })
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCode })
  const isShopManager = !!userInfo?.roles.includes('shop_manager')
  const [inputValue, setInputValue] = useState(merchant.billingFirstName)
  const debouncedInputValue = useDebounceValue(inputValue, 500)
  const payload = useMemo(() => ({
    isShopManager,
    merchantsSearchRequest: {
      platform: Platform.WORDPRESS,
      project_code: projectCode,
      query: debouncedInputValue,
      country_code: countryCode,
      user_id: userInfo?.platform_uid,
    },
    partnerProfileRequest: { code: campaignCode },
  }), [isShopManager, projectCode, countryCode, debouncedInputValue, userInfo, campaignCode])
  const options = useAutocompleteMerchantOptions(payload)

  const handleChange: AutocompleteMerchantProps['onChange'] = useCallbackEvent((option) => {
    onChange?.(option)
    setValue(option)
    if (option?.value && option.billingCountry) {
      setMerchant(option.value, option.label, option.billingCountry)
    }
  })

  const handleInputChange: AutocompleteProps['onInputChange'] = (_, value) => {
    setInputValue(value)
  }

  const clearValue = useCallbackEvent(() => {
    setInputValue('')
    setValue(undefined)
    clearMerchant()
  })

  const handleClear = useCallbackEvent(() => {
    clearValue()
    onChange?.()
  })

  /** 如果不是經銷商，且只有一個選項，則自動選擇第一個選項 */
  useEffect(() => {
    if (!isShopManager && options.length === 1) {
      handleChange(options[0])
    }
  }, [isShopManager, handleChange, options, setValue])

  /** 如果已經選擇過經銷商，但選項中沒有 merchant.index，則清空選項 */
  useEffect(() => {
    if (!options.length || merchant.index === null) return
    if (!options.find(option => option.value === merchant.index)) {
      clearValue()
    }
  }, [clearValue, merchant.index, options])

  return (
    <MuiAutocomplete
      options={options}
      disableClearable
      disabled={!isShopManager}
      renderInput={({ InputProps, ...params }) => (
        <TextField
          placeholder="請選擇經銷商"
          inputRef={inputRef}
          sx={{
            '& .MuiOutlinedInput-root': {
              'backgroundColor': 'white',
              'height': '32px',
              'paddingRight': '9px !important',
              '& .MuiAutocomplete-input': {
                fontSize: '14px',
                padding: '0 0 0 8px !important',
                width: '100%',
              },
            },
          }}
          error={error}
          helperText={helperText}
          InputProps={{
            ...InputProps,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClear} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          {...params}
        />
      )}
      defaultValue={defaultValue}
      value={value}
      onInputChange={handleInputChange}
      onChange={(_, value) => handleChange(value)}
    />
  )
}
